import {ConsentCategory, hasConsent, isDoNotSellRequested} from './consent';

/**
 * Identity and stop conditions for the pre-consent funnel events.
 *
 * Mirrors helpers/anonymousAnalytics.ts in walkthrough-v2-front, and is held to the same
 * conditions counsel set there: coarse funnel events only; a random pseudonymous identifier
 * unrelated to email, customer id or application id; suppressed IP and geolocation; no form
 * values, answers or qualification results; no autocapture or session replay; and no further
 * events after an Analytics decline, opt-out or GPC signal.
 *
 * The identifier deliberately is NOT the authenticated user's email or id. Those are what the
 * identified stream uses, they persist across visits, and they are on the application record —
 * reusing either would make these events linkable to the person and remove the basis for
 * collecting them before a decision at all.
 */
const SESSION_ID_KEY = 'anonymousAnalyticsSessionId';
const DECLINED_KEY = 'analyticsDeclined';

/** Survives a storage failure; reset only by a later grant. */
let declinedThisVisit = false;

/**
 * 128 random bits as hex. Deliberately not the `uuid` package the rest of this app uses: that
 * ships as ESM and this module is reached from the saga, which jest loads untransformed. The
 * value is opaque either way — nothing parses it.
 */
const randomId = (): string => {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);

    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * A random id for this visit. sessionStorage rather than a variable so a reload mid-flow does
 * not split the session in two, and rather than localStorage so it cannot follow the visitor
 * to another visit.
 */
export const getAnonymousSessionId = (): string | null => {
    try {
        const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
        if (existing) return existing;

        const created = randomId();
        window.sessionStorage.setItem(SESSION_ID_KEY, created);
        return created;
    } catch {
        // Private browsing or blocked site data. Without a stable id the events would be
        // unsequenceable noise, so send nothing.
        return null;
    }
};

/** Remembers an explicit decline so later visits stay silent too. */
export const rememberAnalyticsDeclined = (): void => {
    // Held in memory first. A decline is one of counsel's stop conditions, and it has to hold
    // for this visit even when it cannot be written down: private browsing and blocked site data
    // both throw below, and nothing else halts the coarse stream — hasConsent reads false both
    // before a decision and after a decline, so it cannot tell them apart.
    declinedThisVisit = true;

    try {
        window.localStorage.setItem(DECLINED_KEY, 'true');
    } catch {
        // Only the next visit is affected: it re-asks instead of staying silent.
    }
};

export const clearAnalyticsDeclined = (): void => {
    declinedThisVisit = false;

    try {
        window.localStorage.removeItem(DECLINED_KEY);
    } catch {
        // Nothing to do — the flag is an optimisation over Termly's own state.
    }
};

const hasDeclinedAnalytics = (): boolean => {
    if (declinedThisVisit) return true;

    try {
        return window.localStorage.getItem(DECLINED_KEY) === 'true';
    } catch {
        return false;
    }
};

/**
 * Whether a coarse pre-consent event may be sent.
 *
 * False once the visitor has consented (the identified path takes over), once they have
 * declined, and whenever GPC is present — counsel named all three as stop conditions.
 */
export const maySendAnonymousEvent = (): boolean => {
    if (hasConsent(ConsentCategory.analytics)) return false;
    if (hasDeclinedAnalytics()) return false;
    if (isDoNotSellRequested()) return false;

    return true;
};
